import React, { useEffect, useState } from "react";
import {
  fetchAcademyRewardOrdersAPI,
  fetchAcademyRewardProductsAPI,
  fetchRewardAccountAPI,
  refreshRewardBalanceAPI,
  saveRewardAccountAPI,
  selectAcademyRewardProductsAPI,
  selectRecommendedRewardProductsAPI,
  selectAcademyRewardBrandAPI,
  forgetRewardOpen,
  fetchRewardRuleListAPI,
  saveRewardRuleListAPI,
  fetchStudentPointOverviewAPI,
} from "./rewardAPI";
import ProductBrowser, { ProductImage, productPrice } from "./ProductBrowser";
import "./Reward.css";

// 학원 관리 > 포인트 · 기프티콘(2026-10-04).
// 학원이 자기 기프티쇼 계정을 잇고, 기프티쇼 전체 상품 중 학생 상점에 올릴 것을 고른다.
// 쿠폰값은 이 학원의 기프티쇼 비즈머니에서 나간다. 학생에게는 정가만큼 포인트가 든다(1원 = 1P).

const formatNumber = (value) => (value ?? 0).toLocaleString("ko-KR");
const formatDate = (value) => (value ? new Date(value).toLocaleDateString("ko-KR") : "");

const AccountCard = () => {
  const [account, setAccount] = useState(null);
  const [isMock, setIsMock] = useState(false);
  const [form, setForm] = useState({ member_id: "", auth_code: "", auth_token: "", callback_number: "", is_active: false });
  const [balance, setBalance] = useState(null);
  const [message, setMessage] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    fetchRewardAccountAPI()
      .then((data) => {
        setAccount(data.account);
        setIsMock(data.is_mock);
        setForm({
          member_id: data.account.member_id,
          auth_code: data.account.auth_code,
          auth_token: data.account.auth_token,
          callback_number: data.account.callback_number,
          is_active: data.account.is_active,
        });
        setBalance(data.account.last_balance);
      })
      .catch(() => setMessage("계정 정보를 불러오지 못했어요."));
  }, []);

  const save = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await saveRewardAccountAPI(form);
      setAccount(data.account);
      setForm((previous) => ({
        ...previous,
        auth_code: data.account.auth_code,
        auth_token: data.account.auth_token,
        is_active: data.account.is_active,
      }));
      forgetRewardOpen();
      setMessage("저장했어요.");
    } catch {
      setMessage("저장하지 못했어요.");
    }
    setIsBusy(false);
  };

  const refresh = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await refreshRewardBalanceAPI();
      setBalance(data.balance);
      setMessage("연결을 확인했어요.");
    } catch (error) {
      setMessage(error?.response?.data?.error || "연결을 확인하지 못했어요.");
    }
    setIsBusy(false);
  };

  const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));

  return (
    <div className="reward-card">
      <div className="reward-card__title">
        기프티쇼 비즈 연결
        <span>
          {/* 학원이 자기 사업자로 키를 받는 순서와 첨부할 화면 설명 PDF(public/giftishow-guide). nginx 는 폴더 주소를 찾지 못해
              앱 첫 화면을 내주므로 index.html 까지 적는다 */}
          <a className="reward-guide-link" href="/giftishow-guide/index.html" target="_blank" rel="noopener noreferrer">
            키 받는 법 ↗
          </a>{" "}
          {isMock && <span className="reward-badge reward-badge--mock">모의 연결</span>}{" "}
          <span className={`reward-badge ${account?.is_connected ? "reward-badge--on" : "reward-badge--off"}`}>
            {account?.is_connected ? "연결됨" : "연결 안 됨"}
          </span>
        </span>
      </div>
      <div className="reward-form">
        <label>
          회원 ID
          <input value={form.member_id} onChange={update("member_id")} autoComplete="off" />
        </label>
        <label>
          발신번호
          <input value={form.callback_number} onChange={update("callback_number")} inputMode="numeric" />
        </label>
        <label>
          API 인증키
          <input value={form.auth_code} onChange={update("auth_code")} autoComplete="off" />
        </label>
        <label>
          API 인증토큰
          <input value={form.auth_token} onChange={update("auth_token")} autoComplete="off" />
        </label>
      </div>
      <label className="reward-active">
        <input
          type="checkbox"
          checked={Boolean(form.is_active)}
          onChange={(event) => setForm((previous) => ({ ...previous, is_active: event.target.checked }))}
        />
        활성화 (학생 포인트 · 상점 열기)
      </label>
      <div className="reward-actions">
        <button type="button" className="reward-button" onClick={save} disabled={isBusy}>
          저장
        </button>
        <button type="button" className="reward-button reward-button--ghost" onClick={refresh} disabled={isBusy}>
          연결 확인 · 잔액 새로고침
        </button>
        {balance !== null && balance !== undefined && (
          <span className="reward-balance">
            비즈머니 {formatNumber(balance)}원
            {account?.last_balance_checked_at && <small>{formatDate(account.last_balance_checked_at)}</small>}
          </span>
        )}
        {message && <span className="reward-message">{message}</span>}
      </div>
    </div>
  );
};

// 과제를 끝낸 학생에게 유형마다 '몇 점 이상이면 몇 P' 를 준다(2026-10-06 대표). 출제 창이 이 값으로 채우고,
// 출제 때 바꾼 값(이벤트)은 그 출제에만 쓰인다. 수학 계정은 수학 유형만 나온다(서버가 고른다).
const RuleCard = () => {
  const [ruleList, setRuleList] = useState([]);
  // 수학 풀이 사진에 잘함을 매기면 더 주는 포인트(수학 계정만 서버가 내려준다)
  const [workPhotoBonus, setWorkPhotoBonus] = useState(undefined);
  const [message, setMessage] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const applyRules = (data) => {
    setRuleList(data.rule_list);
    setWorkPhotoBonus(data.work_photo_bonus);
  };

  useEffect(() => {
    fetchRewardRuleListAPI()
      .then(applyRules)
      .catch(() => setMessage("불러오지 못했어요."));
  }, []);

  const change = (type, key, text, max) =>
    setRuleList((previous) =>
      previous.map((rule) =>
        rule.type === type ? { ...rule, [key]: text === "" ? "" : Math.min(max, Math.max(0, Number(text))) } : rule
      )
    );

  const save = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      applyRules(await saveRewardRuleListAPI(ruleList, workPhotoBonus === "" ? 0 : workPhotoBonus));
      setMessage("저장했어요.");
    } catch (error) {
      setMessage(error?.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="reward-card">
      <div className="reward-card__title">과제 포인트 기본값</div>
      <div className="reward-rule-list">
        {ruleList.map((rule) => (
          <div key={rule.type} className="reward-rule">
            <span className="reward-rule__name">{rule.name}</span>
            <input
              type="number"
              min={0}
              max={100}
              step={10}
              inputMode="numeric"
              value={rule.min_score}
              onChange={(event) => change(rule.type, "min_score", event.target.value, 100)}
            />
            <span>점 이상</span>
            <input
              type="number"
              min={0}
              step={10}
              inputMode="numeric"
              value={rule.reward_points}
              onChange={(event) => change(rule.type, "reward_points", event.target.value, 100000)}
            />
            <span>P</span>
          </div>
        ))}
      </div>
      {workPhotoBonus !== undefined && (
        <div className="reward-rule reward-rule--bonus">
          <span className="reward-rule__name">풀이 사진 잘함</span>
          <input
            type="number"
            min={0}
            step={10}
            inputMode="numeric"
            value={workPhotoBonus}
            onChange={(event) =>
              setWorkPhotoBonus(event.target.value === "" ? "" : Math.min(100000, Math.max(0, Number(event.target.value))))
            }
          />
          <span>P 더</span>
        </div>
      )}
      <div className="reward-actions">
        <button type="button" className="reward-button" onClick={save} disabled={isBusy || ruleList.length === 0}>
          저장
        </button>
        {message && <span className="reward-message">{message}</span>}
      </div>
    </div>
  );
};

// 학생 상점에 올릴 상품을 기프티쇼 전체에서 고른다(2026-10-12 대표: 학원이 알아서 고른다)
const ACADEMY_ONLY_OPTION_LIST = [
  { value: "selected", label: "올린 것만" },
  { value: "recommended", label: "추천 상품" },
];

const ProductCard = () => {
  const [summary, setSummary] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [message, setMessage] = useState("");

  // 카드를 누르면 바로 저장하지 않고 고른 것만 모아 두었다가 '올리기' 로 한 번에 저장한다(2026-10-26 대표: 올리기 버튼).
  // { 상품 id: { product, updateProduct, isSelected(바뀔 값) } }
  const [pendingById, setPendingById] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const pendingList = Object.values(pendingById);
  const addCount = pendingList.filter((one) => one.isSelected).length;
  const removeCount = pendingList.length - addCount;

  const toggle = (product, updateProduct) =>
    setPendingById((previous) => {
      const next = { ...previous };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = { product, updateProduct, isSelected: !product.is_selected };
      return next;
    });

  const savePending = async () => {
    setIsSaving(true);
    try {
      let data = null;
      for (const isSelected of [true, false]) {
        const idList = pendingList.filter((one) => one.isSelected === isSelected).map((one) => one.product.id);
        if (idList.length) data = await selectAcademyRewardProductsAPI(idList, isSelected);
      }
      pendingList.forEach((one) => one.updateProduct({ is_selected: one.isSelected }));
      setPendingById({});
      if (data) setSummary((previous) => ({ ...previous, selected_count: data.selected_count }));
      setMessage("");
    } catch {
      setMessage("저장하지 못했어요.");
    } finally {
      setIsSaving(false);
    }
  };

  const selectRecommended = async () => {
    try {
      await selectRecommendedRewardProductsAPI();
      setPendingById({});
      setReloadKey((previous) => previous + 1);
    } catch {
      setMessage("저장하지 못했어요.");
    }
  };

  return (
    <div className="reward-card">
      <div className="reward-card__title">
        학생 상점 상품
        <em>{formatNumber(summary?.selected_count)}개 올림</em>
      </div>
      {summary?.recommended_count > 0 && (
        <div className="reward-actions" style={{ marginTop: 0, marginBottom: 12 }}>
          <button type="button" className="reward-button" onClick={selectRecommended}>
            추천 상품 {summary.recommended_count}개 한 번에 올리기
          </button>
        </div>
      )}
      {message && <div className="reward-message">{message}</div>}
      <ProductBrowser
        fetchPage={fetchAcademyRewardProductsAPI}
        onlyOptionList={ACADEMY_ONLY_OPTION_LIST}
        reloadKey={reloadKey}
        onLoaded={setSummary}
        renderBrandActions={(brand, reload) => {
          const isAllSelected = brand.count > 0 && brand.selected_count === brand.count;
          return (
            <button
              type="button"
              className="reward-button"
              onClick={async () => {
                try {
                  const data = await selectAcademyRewardBrandAPI(brand.name, !isAllSelected);
                  setSummary((previous) => ({ ...previous, selected_count: data.selected_count }));
                  setPendingById({});
                  reload();
                } catch {
                  setMessage("저장하지 못했어요.");
                }
              }}
            >
              {isAllSelected ? `${brand.count}개 모두 내리기` : `${brand.count}개 모두 올리기`}
            </button>
          );
        }}
        renderProduct={(product, updateProduct) => {
          const pending = pendingById[product.id];
          const isChecked = pending ? pending.isSelected : product.is_selected;
          return (
          <button
            type="button"
            key={product.id}
            className="reward-product"
            aria-pressed={isChecked}
            data-pending={pending ? "true" : undefined}
            onClick={() => toggle(product, updateProduct)}
          >
            <ProductImage product={product}>
              <span className="reward-check">{isChecked ? "✓" : ""}</span>
            </ProductImage>
            <span className="reward-product__brand">{product.brand_name}</span>
            <span className="reward-product__name">{product.goods_name}</span>
            <span className="reward-product__price">{productPrice(product)}</span>
          </button>
          );
        }}
      />
      {pendingList.length > 0 && (
        <div className="reward-pending-bar">
          <span>
            {addCount > 0 && `${addCount}개 올림`}
            {addCount > 0 && removeCount > 0 && " · "}
            {removeCount > 0 && `${removeCount}개 내림`}
          </span>
          <button type="button" className="reward-button reward-button--ghost" onClick={() => setPendingById({})} disabled={isSaving}>
            취소
          </button>
          <button type="button" className="reward-button" onClick={savePending} disabled={isSaving}>
            {addCount > 0 ? "올리기" : "내리기"}
          </button>
        </div>
      )}
    </div>
  );
};

const OrderCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAcademyRewardOrdersAPI().then(setData).catch(() => setData({ order_list: [], issued_this_month: 0 }));
  }, []);

  if (!data) return null;
  return (
    <div className="reward-card">
      <div className="reward-card__title">
        교환 내역
        <em>이번 달 {formatNumber(data.issued_this_month)}P</em>
      </div>
      {data.order_list.length === 0 ? (
        <div className="reward-empty">아직 교환한 학생이 없어요.</div>
      ) : (
        <table className="reward-table">
          <thead>
            <tr>
              <th>날짜</th>
              <th>학생</th>
              <th>상품</th>
              <th>상태</th>
              <th style={{ textAlign: "right" }}>포인트</th>
            </tr>
          </thead>
          <tbody>
            {data.order_list.map((order) => (
              <tr key={order.id}>
                <td>{formatDate(order.created_at)}</td>
                <td>{order.student_name}</td>
                <td>
                  {order.product.brand_name} {order.product.goods_name}
                </td>
                <td>{order.status_label}</td>
                <td className="reward-table__num">{formatNumber(order.points_used)}P</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// 학생 포인트 한눈에(2026-10-26 대표). 한 줄에 한 학생: 지금 포인트, 이번 달 받은 것 · 쓴 것 · 깎인 것, 마지막 내역.
// 줄을 누르면 그 학생 내역이 펼쳐진다. 서버: reward/views.py TeacherStudentPointOverviewViewSet
const STUDENT_POINT_SORT_LIST = [
  { value: "balance", label: "포인트 많은 순" },
  { value: "earned", label: "이번 달 많이 받은 순" },
  { value: "name", label: "이름 순" },
];

const signedPoint = (amount) => `${amount > 0 ? "+" : ""}${formatNumber(amount)}P`;

const StudentPointCard = () => {
  const [studentList, setStudentList] = useState(null);
  const [sort, setSort] = useState("balance");
  const [keyword, setKeyword] = useState("");
  const [openStudentId, setOpenStudentId] = useState(null);
  const [historyById, setHistoryById] = useState({});

  useEffect(() => {
    fetchStudentPointOverviewAPI()
      .then((data) => setStudentList(data.student_list || []))
      .catch(() => setStudentList([]));
  }, []);

  const toggleStudent = (studentId) => {
    setOpenStudentId((previous) => (previous === studentId ? null : studentId));
    if (!historyById[studentId]) {
      fetchStudentPointOverviewAPI(studentId)
        .then((data) => setHistoryById((previous) => ({ ...previous, [studentId]: data.history || [] })))
        .catch(() => setHistoryById((previous) => ({ ...previous, [studentId]: [] })));
    }
  };

  if (!studentList) return null;
  const shownList = studentList
    .filter((one) => !keyword.trim() || one.name.includes(keyword.trim()))
    .sort((left, right) =>
      sort === "name"
        ? left.name.localeCompare(right.name, "ko")
        : sort === "earned"
        ? right.month_earned - left.month_earned
        : right.balance - left.balance
    );
  const total = studentList.reduce(
    (sum, one) => ({ balance: sum.balance + one.balance, earned: sum.earned + one.month_earned, spent: sum.spent + one.month_spent }),
    { balance: 0, earned: 0, spent: 0 }
  );

  return (
    <div className="reward-card">
      <div className="reward-card__title">
        학생 포인트
        <em>
          모두 {formatNumber(total.balance)}P · 이번 달 받음 {formatNumber(total.earned)}P · 씀 {formatNumber(total.spent)}P
        </em>
      </div>
      <div className="reward-filter">
        <input
          className="reward-point-search"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="학생 이름"
        />
        <select value={sort} onChange={(event) => setSort(event.target.value)}>
          {STUDENT_POINT_SORT_LIST.map((one) => (
            <option key={one.value} value={one.value}>
              {one.label}
            </option>
          ))}
        </select>
      </div>
      {shownList.length === 0 ? (
        <div className="reward-empty">학생이 없어요.</div>
      ) : (
        <table className="reward-table reward-point-table">
          <thead>
            <tr>
              <th>학생</th>
              <th style={{ textAlign: "right" }}>지금</th>
              <th style={{ textAlign: "right" }}>이번 달 받음</th>
              <th style={{ textAlign: "right" }}>씀</th>
              <th style={{ textAlign: "right" }}>깎임</th>
              <th>마지막 내역</th>
            </tr>
          </thead>
          <tbody>
            {shownList.map((one) => (
              <React.Fragment key={one.student_id}>
                <tr
                  className="reward-table__row"
                  aria-selected={openStudentId === one.student_id}
                  onClick={() => toggleStudent(one.student_id)}
                >
                  <td>
                    <b>{one.name}</b>
                  </td>
                  <td className="reward-table__num">{formatNumber(one.balance)}P</td>
                  <td className="reward-table__num reward-point--plus">{one.month_earned ? `+${formatNumber(one.month_earned)}` : "-"}</td>
                  <td className="reward-table__num">{one.month_spent ? formatNumber(one.month_spent) : "-"}</td>
                  <td className="reward-table__num reward-point--minus">{one.month_deducted ? formatNumber(one.month_deducted) : "-"}</td>
                  <td className="reward-point-last">
                    {one.last ? (
                      <>
                        <span>{formatDate(one.last.created_at)}</span> {one.last.description || one.last.type_name}{" "}
                        <b className={one.last.amount > 0 ? "reward-point--plus" : "reward-point--minus"}>{signedPoint(one.last.amount)}</b>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                {openStudentId === one.student_id && (
                  <tr className="reward-point-history">
                    <td colSpan={6}>
                      {!historyById[one.student_id] ? (
                        <div className="reward-empty">불러오는 중이에요.</div>
                      ) : historyById[one.student_id].length === 0 ? (
                        <div className="reward-empty">아직 내역이 없어요.</div>
                      ) : (
                        <ul>
                          {historyById[one.student_id].map((row) => (
                            <li key={row.id}>
                              <span>{formatDate(row.created_at)}</span>
                              <em>{row.type_name}</em>
                              <span className="reward-point-history__text">{row.description}</span>
                              <b className={row.amount > 0 ? "reward-point--plus" : "reward-point--minus"}>{signedPoint(row.amount)}</b>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RewardSettingsPage = () => (
  <div className="reward-page">
    <AccountCard />
    <RuleCard />
    <StudentPointCard />
    <ProductCard />
    <OrderCard />
  </div>
);

export default RewardSettingsPage;
